package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import kotlin.jvm.JvmStatic

/**
 * 下拉刷新装饰器：当 body 已在顶部（scrollY≈0）时继续下拽 → 显示提示；拽超过 [threshold] 释放 →
 * 触发 [onRefresh] 进入刷新中；刷新完成后回到空闲。
 *
 * 状态机：IDLE → PULLING（下拽）→ READY（达 threshold）→ REFRESHING（松手触发 onRefresh）；
 * PULLING 未达 threshold 松手回弹 IDLE；REFRESHING → IDLE（finishRefresh）。
 *
 * 本类只维护状态机与下拽距离，不直接处理鼠标手势（由平台/测试通过 [onPull] / [onRelease] 驱动）；
 * 渲染端按 [state] 与 [pullDistance] 绘制顶部提示区。
 */
class PullToRefresh private constructor() : ScrollDecorator<PullToRefresh>() {

    /** 下拉刷新状态。 */
    enum class State {
        IDLE,
        PULLING,
        READY,
        REFRESHING,
    }

    private var thresholdValue: Float = DEFAULT_THRESHOLD
    private var pullIndicatorHeightValue: Float = DEFAULT_INDICATOR_HEIGHT
    private var pullTextValue: String = "下拉刷新"
    private var releaseTextValue: String = "松手刷新"
    private var refreshingTextValue: String = "刷新中..."

    private var stateValue: State = State.IDLE
    private var pullDistanceValue: Float = 0f
    private var onRefreshCallback: EffectBody? = null

    protected override fun self(): PullToRefresh = this

    // -------- 字段 --------

    fun threshold(px: Float): PullToRefresh {
        thresholdValue = maxOf(MIN_THRESHOLD, px)
        return this
    }

    fun threshold(): Float = thresholdValue

    fun pullIndicatorHeight(px: Float): PullToRefresh {
        pullIndicatorHeightValue = maxOf(0f, px)
        return this
    }

    fun pullIndicatorHeight(): Float = pullIndicatorHeightValue

    fun pullText(t: String?): PullToRefresh {
        pullTextValue = t ?: ""
        return this
    }

    fun pullText(): String = pullTextValue

    fun releaseText(t: String?): PullToRefresh {
        releaseTextValue = t ?: ""
        return this
    }

    fun releaseText(): String = releaseTextValue

    fun refreshingText(t: String?): PullToRefresh {
        refreshingTextValue = t ?: ""
        return this
    }

    fun refreshingText(): String = refreshingTextValue

    /** 设置刷新回调（由 IR 层把 DSL fun 包成 EffectBody 注入；common 无 Runnable，用既有 SAM）。 */
    fun onRefresh(callback: EffectBody?): PullToRefresh {
        onRefreshCallback = callback
        return this
    }

    // -------- 状态 --------

    fun state(): State = stateValue

    fun pullDistance(): Float = pullDistanceValue

    fun refreshing(): Boolean = stateValue == State.REFRESHING

    /** 当前应显示的提示文本（按状态）。 */
    fun currentText(): String = when (stateValue) {
        State.PULLING -> pullTextValue
        State.READY -> releaseTextValue
        State.REFRESHING -> refreshingTextValue
        State.IDLE -> ""
    }

    // -------- 状态机驱动 --------

    /**
     * 下拽：累加下拽距离并在 PULLING/READY 间切换。仅当 body 在顶部时有意义。刷新中忽略后续下拽。
     *
     * @param deltaPull 本次下拽增量（像素，正值=下拽）
     */
    fun onPull(deltaPull: Float): PullToRefresh {
        if (stateValue == State.REFRESHING) {
            return this
        }
        pullDistanceValue = maxOf(0f, pullDistanceValue + deltaPull)
        stateValue = when {
            pullDistanceValue <= 0f -> State.IDLE
            pullDistanceValue >= thresholdValue -> State.READY
            else -> State.PULLING
        }
        return this
    }

    /** 松手：READY 时进入 REFRESHING 并触发 onRefresh；否则回弹到 IDLE。 */
    fun onRelease(): PullToRefresh {
        if (stateValue == State.READY) {
            stateValue = State.REFRESHING
            pullDistanceValue = pullIndicatorHeightValue
            onRefreshCallback?.run()
        } else if (stateValue != State.REFRESHING) {
            stateValue = State.IDLE
            pullDistanceValue = 0f
        }
        return this
    }

    /** 刷新完成：由业务在数据加载完毕后调用，回到空闲并回弹。 */
    fun finishRefresh(): PullToRefresh {
        stateValue = State.IDLE
        pullDistanceValue = 0f
        return this
    }

    companion object {
        /** 触发刷新的默认下拽阈值（像素）。 */
        private const val DEFAULT_THRESHOLD = 60f

        /** 刷新中提示区默认高度（像素）。 */
        private const val DEFAULT_INDICATOR_HEIGHT = 24f

        /** 阈值下限（像素），防止设为 0 导致即时触发。 */
        private const val MIN_THRESHOLD = 1f

        /** 创建一个下拉刷新装饰器。 */
        @JvmStatic
        fun create(): PullToRefresh = PullToRefresh()
    }
}
